/**
 * ReportsCampusPulse — "Campus Pulse", the student-engagement report.
 *
 * Opens on cohort aggregates for a department head and drills to one student's
 * record. Reading order: what the cohort did (KPIs) → when they did it (the
 * semester ladder) → who they are (by program, then the roster) → then one
 * student, the same ladder at n=1.
 *
 * Deliberately unopinionated: it reports counts, medians and a student's
 * position against their own program. It never scores a student.
 *
 * Data: ONE call to `getCampusPulseReport` (JAI-1403 v1.1,
 * POST /v1/admin/reports/campus-pulse/result). Nothing is measured in this
 * browser. Cohorts, KPIs, findings, breakdowns, the engagement frame, the
 * coverage block and a PAGED roster all come from the server, and
 * campus-pulse-data.ts maps them to the shapes below. Filters, sorting, paging,
 * search and the finding/quadrant scope are all request parameters, so every
 * number on screen describes the set the reader asked for.
 * Route: reports.pulse
 */
import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties, type ReactNode } from "react";
import { Btn, Card, Icon, T } from "@v0y4ge/jrni-ui";
import { Screen, PageHeader } from "../../shell/chrome";
import { DataSourceStrip } from "../../shell/opSource";
import { useNav } from "../../shell/router";
import { useTenant, can, isAdmin, useUser } from "../../shell/access";
import ConfirmModal from "../../shell/ConfirmModal";
import { toast } from "../../shell/toast";
import { logAction } from "../../lib/audit";
import { useT } from "../../lib/i18n";
import { studioRequestOptions } from "../../lib/studio-config";
import { useWfResource } from "../workforce/useWorkforce";
import { WfPending } from "../workforce/wf-shared";
import { customerItemAction } from "../customers/customers-api";
import { getCampusPulseReport } from "@v0y4ge/api-client";
import type {
  CampusPulseQuadrantKey, CampusPulseReport, CampusPulseRosterSortKey,
} from "@v0y4ge/api-contracts";
import { reportTabs } from "./ReportsOverview";
import {
  buildView, buildFindings, whyFlagged, toParameters, coverageRows,
  LANES, DEFAULT_FILTERS, ROSTER_PAGE, VERDICT_LABEL, QUADRANT_LABEL, QUADRANT_NOTE,
  dLong,
  type Filters, type Lane, type Scope, type Student, type View, type Finding,
} from "./campus-pulse-data";
import {
  FindingCard, SEV_ACCENT, SEVERITY_LABEL, CATEGORY_LABEL,
} from "./findings-kit";

/* The one operation this screen reads. The admin catalogue reads it used to
 * assemble client-side (listCustomers / listStaff / listLocations /
 * getServiceCatalogue) are gone: the report is composed server-side. */
const OPS = ["getCampusPulseReport"];
const HANDOFF_UNAVAILABLE = "Assigning reviews and sending findings to an owner are not available yet. No hand-off has been recorded.";

const LANE_C: Record<Lane, string> = { appt: T.p1, walkin: T.orange1, event: T.tPurple, noshow: T.red1 };
const SUP_C = T.orange1;
const GRO_C = T.tTurquoise;

const TH: CSSProperties = {
  textAlign: "left", padding: "9px 12px", fontSize: 10, textTransform: "uppercase",
  letterSpacing: "0.07em", color: T.soft, fontWeight: 600, whiteSpace: "nowrap",
  borderBottom: `1px solid ${T.border}`, background: T.white, position: "sticky", top: 0, zIndex: 2,
};
const TD: CSSProperties = { padding: "9px 12px", fontSize: 12.5, borderBottom: `1px solid ${T.n6}`, whiteSpace: "nowrap", color: T.n2 };
const MONO: CSSProperties = { fontFamily: T.fontMono, fontVariantNumeric: "tabular-nums" };
const EYEBROW: CSSProperties = { fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", color: T.soft };
const CARD_TITLE: CSSProperties = { fontSize: 13.5, fontWeight: 700, color: T.n1 };
const SECTION_HEAD: CSSProperties = { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, padding: "11px 16px", borderBottom: `1px solid ${T.border}` };
const WRAP: CSSProperties = { background: T.white, border: `1px solid ${T.border}`, borderRadius: 10, overflow: "hidden", display: "flex", flexDirection: "column", minWidth: 0 };
const PRETTY = { textWrap: "pretty" } as CSSProperties;

const plural = (n: number, w: string) => `${n} ${w}${n === 1 ? "" : "s"}`;
const num = (n: number | null | undefined, dp = 1) => (n === null || n === undefined ? "—" : (Math.round(n * 10 ** dp) / 10 ** dp).toFixed(dp));
const pct = (x: number | null | undefined) => (x === null || x === undefined ? "—" : `${Math.round(x * 100)}%`);
const int = (n: number | null | undefined) => (n === null || n === undefined ? "—" : n.toLocaleString());

/** A dimension the report says it could not measure. Never a zero, never a
 *  blank cell: the reason the server gave, in the place the number would be. */
function NotMeasured({ why, compact }: { why: string; compact?: boolean }) {
  return (
    <span style={{ display: "flex", alignItems: "flex-start", gap: 6, fontSize: compact ? 10.5 : 11.5, lineHeight: 1.45, color: T.soft, ...PRETTY }}>
      <span style={{ display: "inline-flex", flexShrink: 0, marginTop: 1, color: T.n4 }}><Icon.info size={compact ? 11 : 12} /></span>
      <span>{why}</span>
    </span>
  );
}

/* ── Small parts ───────────────────────────────────────────────────── */
function Bar({ segs, height = 7, radius = 3.5 }: { segs: { v: number; c: string; label: string; o?: number }[]; height?: number; radius?: number }) {
  const t = useT();
  const total = segs.reduce((a, s) => a + Math.max(0, s.v), 0) || 1;
  return (
    <div style={{ display: "flex", height, borderRadius: radius, overflow: "hidden", background: T.n6, width: "100%" }}>
      {segs.map((s, i) => (
        <div key={i} title={s.label ? t("reports.campusPulse.title6", "{label}: {arg2}", { label: s.label, arg2: s.v.toLocaleString() }) : undefined}
          style={{ width: `${(Math.max(0, s.v) / total) * 100}%`, background: s.c, opacity: s.o ?? 1 }} />
      ))}
    </div>
  );
}

/** Terms as bars, not a line — a count of zero has to read as an empty
 *  column rather than as a point on a slope. */
function Spark({ values, max, w = 64, h = 18, color = T.p1 }: { values: number[]; max?: number; w?: number; h?: number; color?: string }) {
  const t = useT();
  if (!values.length) return <span style={{ fontSize: 10.5, color: T.soft }}>—</span>;
  const top = Math.max(1, max ?? Math.max(...values));
  const gap = 2;
  const bw = Math.max(2, (w - gap * (values.length - 1)) / values.length);
  return (
    <svg width={w} height={h} style={{ display: "block" }} role="img" aria-label={t("reports.campusPulse.ariaLabel", "Interactions per term: {arg1}", { arg1: values.join(", ") })}>
      {values.map((v, i) => {
        const bh = v ? Math.max(2, (v / top) * h) : 1;
        return <rect key={i} x={i * (bw + gap)} y={h - bh} width={bw} height={bh} rx={1} fill={v ? color : T.n5} opacity={v ? 1 : 0.5} />;
      })}
    </svg>
  );
}

function TrendCell({ slope }: { slope: number | null }) {
  const t = useT();
  if (slope === null) return <span style={{ ...MONO, fontSize: 11.5, color: T.soft }}>—</span>;
  const dir = slope > 0.15 ? "rising" : slope < -0.15 ? "falling" : "flat";
  const c = dir === "rising" ? T.green1 : dir === "falling" ? T.red1 : T.soft;
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 5 }} title={t("reports.campusPulse.title7", "Least-squares slope of interactions per term: {arg1}", { arg1: num(slope) })}>
      <span style={{ ...MONO, fontSize: 11.5, color: c, fontWeight: 600 }}>{slope > 0 ? "+" : ""}{num(slope)}</span>
      <span style={{ fontSize: 9, color: c }}>{dir === "rising" ? "▲" : dir === "falling" ? "▼" : "—"}</span>
    </span>
  );
}

function Select<V extends string>({ value, options, onChange, minWidth, searchable, searchHint, disabled, disabledHint }: {
  value: V; options: { id: V; label: string; meta?: string }[]; onChange: (v: V) => void;
  minWidth?: number; searchable?: boolean; searchHint?: string; disabled?: boolean; disabledHint?: string;
}) {
  const t = useT();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const ref = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);
  const current = options.find((o) => o.id === value);
  const shown = q ? options.filter((o) => o.label.toLowerCase().includes(q.toLowerCase())) : options;
  return (
    <div ref={ref} style={{ position: "relative", minWidth }}>
      <button
        onClick={() => { if (!disabled) { setOpen(!open); setQ(""); } }}
        disabled={disabled}
        title={disabled ? disabledHint : undefined}
        style={{
          display: "flex", alignItems: "center", gap: 7, width: "100%", height: 30, padding: "0 10px",
          borderRadius: 8, border: `1px solid ${T.border}`, background: disabled ? T.n6 : T.white,
          color: disabled ? T.soft : T.n1, fontFamily: "inherit", fontSize: 12,
          cursor: disabled ? "not-allowed" : "pointer", textAlign: "left",
        }}>
        <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{current ? current.label : "—"}</span>
        <span style={{ display: "inline-flex", color: T.soft, flexShrink: 0 }}><Icon.chev size={11} /></span>
      </button>
      {open && !disabled ? (
        <div style={{ position: "absolute", top: 34, left: 0, minWidth: "100%", maxHeight: 280, overflowY: "auto", background: T.white, border: `1px solid ${T.border}`, borderRadius: 8, boxShadow: "0 8px 24px rgba(0,0,0,0.10)", zIndex: 90, padding: 4 }}>
          {searchable ? (
            <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder={searchHint || t("reports.campusPulse.placeholder", "Search…")}
              style={{ width: "100%", boxSizing: "border-box", border: `1px solid ${T.border}`, borderRadius: 6, padding: "5px 8px", fontFamily: "inherit", fontSize: 12, marginBottom: 4, outline: "none" }} />
          ) : null}
          {shown.map((o) => (
            <button key={o.id} onClick={() => { onChange(o.id); setOpen(false); }}
              style={{ display: "flex", alignItems: "baseline", gap: 8, width: "100%", padding: "6px 8px", border: 0, borderRadius: 6, background: o.id === value ? T.p4 : "transparent", cursor: "pointer", fontFamily: "inherit", fontSize: 12, color: T.n1, textAlign: "left" }}>
              <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{o.label}</span>
              {o.meta ? <span style={{ ...MONO, fontSize: 10, color: T.soft, flexShrink: 0 }}>{o.meta}</span> : null}
            </button>
          ))}
          {!shown.length ? <div style={{ padding: "8px 10px", fontSize: 11.5, color: T.soft }}>{t("reports.campusPulse.text8", "Nothing matches.")}</div> : null}
        </div>
      ) : null}
    </div>
  );
}

function Chip({ on, onClick, children }: { on: boolean; onClick: () => void; children: ReactNode }) {
  return (
    <button onClick={onClick}
      style={{ display: "inline-flex", alignItems: "center", gap: 6, height: 27, padding: "0 11px", borderRadius: 999, border: `1px solid ${on ? T.p1 : T.border}`, background: on ? T.p1 : T.white, color: on ? T.onColor : T.n2, fontFamily: "inherit", fontSize: 11.5, fontWeight: 600, cursor: "pointer" }}>
      {children}
    </button>
  );
}

/* ── The engagement frame ───────────────────────────────────────────── */
function FrameCard({ v, activeQuad, onQuad }: { v: View; activeQuad: CampusPulseQuadrantKey | null; onQuad: (k: CampusPulseQuadrantKey | null) => void }) {
  const t = useT();
  const support = v.depts.filter((d) => d.kind === "support").map((d) => d.label);
  const growth = v.depts.filter((d) => d.kind === "growth").map((d) => d.label);

  /* `frame` is null exactly when the department-kind dimension is unavailable,
     and coverage.notes says why. A one-axis frame would be a different,
     unlabelled thing, so the card says so instead of drawing one. */
  if (!v.frame) {
    const why = v.coverage.notes.find((n) => /department/i.test(n))
      ?? "Departments carry no support-or-growth classification in this tenant, so the volume axis has nothing to be read against.";
    return (
      <Card style={{ padding: "13px 15px", display: "flex", flexDirection: "column", gap: 9, minWidth: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <span style={{ ...MONO, fontSize: 9.5, fontWeight: 700, width: 16, height: 16, borderRadius: 4, background: T.n6, color: T.soft, display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>1</span>
          <span style={{ ...EYEBROW, fontSize: 9.5 }}>{t("reports.campusPulse.text9", "What high and low engagement mean")}</span>
        </div>
        <NotMeasured why={why} />
      </Card>
    );
  }

  const cell = (q: { key: CampusPulseQuadrantKey; students: number; share: number | null }, c: string) => {
    const on = activeQuad === q.key;
    return (
      <button key={q.key} onClick={() => onQuad(on ? null : q.key)} disabled={!q.students}
        title={q.students ? t("reports.campusPulse.title8", "Scope the whole report to these {students}", { students: q.students }) : t("reports.campusPulse.title9", "Nobody in this quadrant")}
        style={{ display: "flex", flexDirection: "column", gap: 2, padding: "8px 9px", borderRadius: 6, background: on ? T.p4 : T.n6, border: `1px solid ${on ? T.p1 : "transparent"}`, minWidth: 0, cursor: q.students ? "pointer" : "default", textAlign: "left", fontFamily: "inherit" }}>
        <span style={{ ...MONO, fontSize: 17, fontWeight: 700, color: c, lineHeight: 1.1 }}>{q.students}</span>
        <span style={{ fontSize: 11, color: T.n1, fontWeight: 600, lineHeight: 1.25 }}>{QUADRANT_LABEL[q.key]}</span>
        <span style={{ fontSize: 10, lineHeight: 1.35, color: T.soft }}>{QUADRANT_NOTE[q.key]}</span>
      </button>
    );
  };
  const COLOR: Record<CampusPulseQuadrantKey, string> = {
    heavy_support: SUP_C, building: GRO_C, quiet_struggling: SUP_C, low_touch: T.soft,
  };

  return (
    <Card style={{ padding: "13px 15px", display: "flex", flexDirection: "column", gap: 10, minWidth: 0, border: `1px solid ${activeQuad ? T.p1 : T.border}` }}>
      <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <span style={{ ...MONO, fontSize: 9.5, fontWeight: 700, width: 16, height: 16, borderRadius: 4, background: T.n6, color: T.soft, display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>1</span>
          <span style={{ ...EYEBROW, fontSize: 9.5 }}>{t("reports.campusPulse.text9", "What high and low engagement mean")}</span>
          <span style={{ marginLeft: "auto", fontSize: 10, color: T.soft }}>{t("reports.campusPulse.text10", "click a quadrant to scope")}</span>
        </div>
        <span style={{ fontSize: 12, lineHeight: 1.45, color: T.n2, ...PRETTY }}>
          {t("reports.campusPulse.text11", "Volume alone says nothing. Read it against the blend:")}{plural(support.length, "support desk")}
          {support.length ? t("reports.campusPulse.text12", "({arg1}…)", { arg1: support.slice(0, 3).join(", ").toLowerCase() }) : ""} {t("reports.campusPulse.text13", "are visited because something is wrong; growth desks")}{growth.length ? t("reports.campusPulse.text12", "({arg1}…)", { arg1: growth.slice(0, 2).join(", ").toLowerCase() }) : ""} {t("reports.campusPulse.text14", "because something is going right.")}</span>
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 7 }}>
        {v.frame.quadrants.map((q) => cell(q, COLOR[q.key]))}
      </div>
      <span style={{ ...MONO, fontSize: 10, color: T.soft, lineHeight: 1.4 }}>
        {t("reports.campusPulse.text15", "Split on the cohort’s own medians:")}{num(v.frame.medianInteractions)} {t("reports.campusPulse.text16", "interactions,")}{pct(v.frame.medianSupportShare)} {t("reports.campusPulse.text17", "support share. n=")}{v.n}.
      </span>
    </Card>
  );
}

function FindingsBand({ v, fnds, cols, activeFindingId, activeQuad, onFinding, onQuad, verbStates, onVerb, busyVerb, canAct, staffNamesVisible, filtered }: {
  v: View; fnds: Finding[]; cols: number;
  activeFindingId: string | null; activeQuad: CampusPulseQuadrantKey | null;
  onFinding: (f: Finding | null) => void; onQuad: (k: CampusPulseQuadrantKey | null) => void;
  verbStates: Record<string, { state: "done"; n: number; at: number }>;
  onVerb: (f: Finding) => void; busyVerb: string | null; canAct: boolean; staffNamesVisible: boolean;
  filtered: boolean;
}) {
  const t = useT();
  const [open, setOpen] = useState(() => { try { return window.localStorage.getItem("cp-actions-open") !== "0"; } catch { return true; } });
  useEffect(() => { try { window.localStorage.setItem("cp-actions-open", open ? "1" : "0"); } catch { /* ignore */ } }, [open]);

  if (!v.n) {
    return (
      <Card style={{ padding: "16px 18px", display: "flex", flexDirection: "column", gap: 5 }}>
        <span style={EYEBROW}>{t("reports.campusPulse.text18", "Findings")}</span>
        <span style={{ fontSize: 12.5, color: T.n2 }}>{t("reports.campusPulse.text19", "No students match this scope. Widen the filters and every finding recomputes against whatever is left.")}</span>
      </Card>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
        <span style={{ display: "flex", alignItems: "center", gap: 9, flexWrap: "wrap", minWidth: 0 }}>
          <button onClick={() => setOpen(!open)} title={open ? t("reports.campusPulse.title10", "Minimize findings") : t("reports.campusPulse.title11", "Show findings")}
            style={{ display: "flex", alignItems: "center", gap: 6, border: 0, background: "transparent", padding: "2px 4px 2px 0", margin: 0, cursor: "pointer", fontFamily: "inherit", color: T.soft }}>
            <span style={{ display: "inline-flex", transform: open ? "rotate(0deg)" : "rotate(-90deg)", transition: "transform 120ms ease" }}><Icon.chev size={12} /></span>
            <span style={{ ...EYEBROW, color: T.n1 }}>{t("reports.campusPulse.text18", "Findings")}</span>
          </button>
          <span style={{ fontSize: 11, color: T.soft }}>{fnds.length} {t("reports.campusPulse.text20", "findings · the server thresholds these numbers and prints the method it used · one verb per card")}</span>
          {filtered ? <span style={{ ...MONO, fontSize: 9.5, fontWeight: 700, letterSpacing: "0.05em", textTransform: "uppercase", color: T.p1, background: T.p4, padding: "2px 7px", borderRadius: 999 }}>{t("reports.campusPulse.text21", "filtered")}</span> : null}
        </span>
        {!open ? <Btn variant="ghost" onClick={() => setOpen(true)}>{t("reports.campusPulse.text22", "Show")}{plural(fnds.length, "finding")}</Btn> : null}
      </div>

      {!open ? (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 0, padding: 0, background: T.white, border: `1px solid ${T.border}`, borderRadius: 10, overflow: "hidden" }}>
          {fnds.map((f, i) => (
            <button key={f.id} onClick={() => { setOpen(true); }} title={t("reports.campusPulse.title12", "{arg1} · {arg2}", { arg1: SEVERITY_LABEL[f.severity], arg2: CATEGORY_LABEL[f.category] })}
              style={{ display: "flex", alignItems: "baseline", gap: 8, padding: "10px 16px", borderLeft: i ? `1px solid ${T.n6}` : 0, border: 0, background: "transparent", flex: "1 1 auto", minWidth: 0, cursor: "pointer", fontFamily: "inherit", textAlign: "left" }}>
              <span style={{ ...MONO, fontSize: 17, fontWeight: 700, color: SEV_ACCENT[f.severity] }}>{f.value}</span>
              <span style={{ fontSize: 11.5, color: T.soft, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{f.headline}</span>
            </button>
          ))}
        </div>
      ) : (
        <>
          <div style={{ display: "grid", gridTemplateColumns: `repeat(${Math.min(3, cols)}, minmax(0, 1fr))`, gap: 12, alignItems: "stretch" }}>
            <FrameCard v={v} activeQuad={activeQuad} onQuad={onQuad} />
            {fnds.map((f) => (
              <FindingCard key={f.id} f={f} verb={f.verb} filterable={f.scope !== null && (f.memberCount ?? 0) > 0}
                active={activeFindingId === f.id}
                onToggle={() => onFinding(activeFindingId === f.id ? null : f)}
                verbState={f.verb?.kind === "handoff" ? undefined : verbStates[f.id]} onVerb={() => onVerb(f)}
                busy={busyVerb === f.id}
                canAct={canAct && !f.verbBlocked}
                actDenyReason={f.verbBlocked ?? "Your role can read this report but not send on its behalf"} />
            ))}
          </div>
          {staffNamesVisible ? (
            <div style={{ display: "flex", gap: 8, alignItems: "flex-start", padding: "10px 14px", background: T.orange2, border: `1px solid ${T.border}`, borderRadius: 10 }}>
              <span style={{ color: T.orange1, display: "inline-flex", flexShrink: 0, marginTop: 1 }}><Icon.info size={13} /></span>
              <span style={{ fontSize: 11.5, lineHeight: 1.5, color: T.n1, ...PRETTY }}>
                {t("reports.campusPulse.text23", "Advisor comparisons on these cards are")}<strong>{t("reports.campusPulse.text24", "workload evidence, not performance ratings")}</strong>{t("reports.campusPulse.text25", ". Bookable hours come from each advisor’s own location record and caseload from the enrollment assignment; neither measures how well anyone advises. Visibility of staff comparisons is a role question — readers without the advisor-roster capability see the same spread with the names withheld.")}</span>
            </div>
          ) : null}
        </>
      )}
    </div>
  );
}

/* ── Cohort sections ───────────────────────────────────────────────── */
function Kpi({ label, value, unit }: { label: string; value: ReactNode; unit?: string }) {
  return (
    <Card style={{ padding: "13px 15px", display: "flex", flexDirection: "column", gap: 3, minWidth: 0 }}>
      <div style={{ ...EYEBROW, marginBottom: 2, overflow: "hidden", textOverflow: "ellipsis" }}>{label}</div>
      <div style={{ display: "flex", alignItems: "baseline", gap: 3 }}>
        <span style={{ ...MONO, fontSize: 25, fontWeight: 800, color: T.n1 }}>{value}</span>
        {unit ? <span style={{ fontSize: 12, color: T.soft, fontWeight: 600 }}>{unit}</span> : null}
      </div>
    </Card>
  );
}

function SortTh({ id, label, sort, dir, onSort, align, width, title }: {
  id: CampusPulseRosterSortKey; label: string; sort: CampusPulseRosterSortKey; dir: string;
  onSort: (id: CampusPulseRosterSortKey) => void; align?: "left" | "right"; width?: number; title?: string;
}) {
  const t = useT();
  const on = sort === id;
  return (
    <th style={{ ...TH, textAlign: align || "left", width, cursor: "pointer", color: on ? T.n1 : TH.color }} onClick={() => onSort(id)} title={title || t("reports.campusPulse.title13", "Sort by {label}", { label })}>
      <span style={{ display: "flex", alignItems: "center", gap: 4, justifyContent: align === "right" ? "flex-end" : "flex-start" }}>
        <span>{label}</span>
        <span style={{ fontSize: 7, opacity: on ? 1 : 0.25 }}>{on ? (dir === "asc" ? "▲" : "▼") : "▼"}</span>
      </span>
    </th>
  );
}

function Handle({ onArm, onDisarm, active }: { onArm: () => void; onDisarm: () => void; active: boolean }) {
  const t = useT();
  return (
    <button onMouseDown={onArm} onMouseUp={onDisarm} onMouseLeave={onDisarm} title={t("reports.campusPulse.title14", "Drag to reorder this section")}
      style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: 18, height: 18, border: 0, background: "transparent", color: active ? T.p1 : T.n5, cursor: "grab", padding: 0, flexShrink: 0 }}>
      <span style={{ fontSize: 12, lineHeight: 1 }}>⠿</span>
    </button>
  );
}

/**
 * The semester ladder. v1.1 reports interactions, active students and dark
 * students PER TERM; it does not break a term down by lane, so the mix column
 * and the four lane columns the client-side model used to fill are gone rather
 * than filled with a number the report never measured.
 */
function TermLadder({ v, handle }: { v: View; handle: ReactNode }) {
  const t = useT();
  const max = Math.max(1, ...v.ladder.map((t) => t.total));
  if (!v.ladder.length) {
    return (
      <div style={WRAP}>
        <div style={SECTION_HEAD}><span style={{ display: "flex", alignItems: "center", gap: 10 }}>{handle}<span style={CARD_TITLE}>{t("reports.campusPulse.text26", "Academic semester")}</span></span></div>
        <div style={{ padding: "16px 18px" }}>
          <NotMeasured why="This tenant has no academic terms configured, so there is no ladder to place interactions on. Every other number on this page still describes the window." />
        </div>
      </div>
    );
  }
  return (
    <div style={WRAP}>
      <div style={SECTION_HEAD}>
        <span style={{ display: "flex", alignItems: "center", gap: 10 }}>{handle}<span style={CARD_TITLE}>{t("reports.campusPulse.text26", "Academic semester")}</span></span>
        <span style={{ fontSize: 10, color: T.soft }}>{v.completeTerms} {t("reports.campusPulse.text27", "of")}{v.totalTerms} {t("reports.campusPulse.text28", "terms complete")}</span>
      </div>
      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 640 }}>
          <thead>
            <tr>
              <th style={{ ...TH, width: 150 }}>{t("reports.campusPulse.text29", "Term")}</th>
              <th style={{ ...TH, minWidth: 150 }}>{t("reports.campusPulse.text30", "Interactions")}</th>
              <th style={{ ...TH, textAlign: "right" }}>{t("reports.campusPulse.text31", "Total")}</th>
              <th style={{ ...TH, textAlign: "right" }}>{t("reports.campusPulse.text32", "Per student")}</th>
              <th style={{ ...TH, textAlign: "right" }} title={t("reports.campusPulse.title15", "Students with at least one interaction in this term")}>{t("reports.campusPulse.text33", "Active")}</th>
              <th style={{ ...TH, textAlign: "right" }} title={t("reports.campusPulse.title16", "Students silent for the whole term. Null for the first term — there is no prior window to have gone dark from.")}>{t("reports.campusPulse.text34", "Dark")}</th>
            </tr>
          </thead>
          <tbody>
            {v.ladder.map((term) => (
              <tr key={term.key} style={{ background: term.complete ? "transparent" : T.n6 }}>
                <td style={{ ...TD, color: T.n1, fontWeight: 500 }}>
                  <span style={{ display: "flex", alignItems: "center", gap: 7 }}>
                    <span>{term.label}</span>
                    {!term.complete ? <span style={{ fontSize: 9, fontWeight: 700, color: T.soft, background: T.n6, border: `1px solid ${T.border}`, borderRadius: 999, padding: "1px 6px" }}>{term.started ? t("reports.campusPulse.text35", "in progress") : t("reports.campusPulse.text36", "not started")}</span> : null}
                  </span>
                </td>
                <td style={{ ...TD }}>
                  {term.total > 0
                    ? <div style={{ width: `${Math.max(14, (term.total / max) * 100)}%` }}><Bar height={9} segs={[{ v: term.total, c: LANE_C.appt, label: t("reports.campusPulse.label", "Interactions") }]} /></div>
                    : <span style={{ fontSize: 11, color: T.soft }}>{t("reports.campusPulse.text37", "no data yet")}</span>}
                </td>
                <td style={{ ...TD, ...MONO, textAlign: "right", color: T.n1, fontWeight: 600 }}>{term.total ? term.total.toLocaleString() : "—"}</td>
                <td style={{ ...TD, ...MONO, textAlign: "right" }}>{term.total ? term.perStudent.toFixed(2) : "—"}</td>
                <td style={{ ...TD, ...MONO, textAlign: "right" }}>{term.active ? term.active.toLocaleString() : "—"}</td>
                <td style={{ ...TD, ...MONO, textAlign: "right", color: term.dark ? T.red1 : "inherit" }}>{term.dark === null ? "—" : term.dark.toLocaleString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <div style={{ padding: "8px 16px 12px", display: "flex", flexDirection: "column", gap: 6 }}>
          {v.outsideTerms === null ? null : v.outsideTerms > 0 ? (
            /* The ladder counts only interactions inside a term window. Winter and
               summer contact is real and stays in the totals above, so the
               difference is stated here rather than left as a silent mismatch. */
            <p style={{ ...MONO, fontSize: 10, color: T.soft, margin: 0 }}>
              {t("reports.campusPulse.text38", "Term rows sum to")}{(v.interactions - v.outsideTerms).toLocaleString()} {t("reports.campusPulse.text27", "of")}{v.interactions.toLocaleString()} {t("reports.campusPulse.text39", "interactions")}{" — "}{v.outsideTerms.toLocaleString()} {t("reports.campusPulse.text40", "fell between terms and belong to no row.")}</p>
          ) : null}
          <NotMeasured compact why="Interaction type is not broken down per term by this report — the mix below is for the whole window." />
        </div>
      </div>
    </div>
  );
}

function TypePanel({ v, handle }: { v: View; handle: ReactNode }) {
  const t = useT();
  /* Share is taken over the three MODALITIES only. No-shows are an OUTCOME, not
     a fourth channel, and adding them to the denominator would make every share
     describe a set that does not exist. */
  const modalityLanes = LANES.filter((l) => l.id !== "noshow");
  const total = modalityLanes.reduce((a, l) => a + v.laneCount[l.id], 0) || 1;
  return (
    <div style={WRAP}>
      <div style={SECTION_HEAD}>
        <span style={{ display: "flex", alignItems: "center", gap: 10 }}>{handle}<span style={CARD_TITLE}>{t("reports.campusPulse.text41", "Interaction type")}</span></span>
        <span style={{ fontSize: 10, color: T.soft }}>{t("reports.campusPulse.text42", "share of all contact")}</span>
      </div>
      <div style={{ display: "flex", flexDirection: "column", padding: "12px 16px 14px", gap: 12 }}>
        {modalityLanes.map((l) => {
          const n = v.laneCount[l.id];
          return (
            <div key={l.id} style={{ display: "flex", flexDirection: "column", gap: 5 }}>
              <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12 }}>
                <span style={{ display: "flex", alignItems: "center", gap: 6, color: T.n2 }}>
                  <span style={{ width: 7, height: 7, borderRadius: 2, background: LANE_C[l.id] }} />{l.label}
                </span>
                <span style={{ ...MONO, color: T.n1, fontWeight: 600 }}>{n.toLocaleString()} <span style={{ color: T.soft, fontWeight: 400 }}>{Math.round((n / total) * 100)}%</span></span>
              </div>
              <Bar height={5} segs={[{ v: n, c: LANE_C[l.id], label: l.label }, { v: total - n, c: "transparent", label: "" }]} />
            </div>
          );
        })}
        <div style={{ borderTop: `1px solid ${T.n6}`, paddingTop: 10 }}>
          <NotMeasured compact why="No-shows are an outcome rather than a channel, so they are not a share of this total. Untick the No-shows chip above to re-measure the whole report without them." />
        </div>
      </div>
    </div>
  );
}

/**
 * The coverage block — what the report could NOT answer, in the report's own
 * words. This is the block the whole server-side switch was made for, and it
 * replaces the client-side trend split, which the report does not carry.
 */
function CoveragePanel({ v, handle }: { v: View; handle: ReactNode }) {
  const t = useT();
  const rows = coverageRows(v);
  const TONE: Record<string, { bg: string; fg: string }> = {
    available: { bg: T.green2, fg: T.green1 },
    partial: { bg: T.orange2, fg: T.orange1 },
    unavailable: { bg: T.red2, fg: T.red1 },
  };
  return (
    <div style={WRAP}>
      <div style={SECTION_HEAD}>
        <span style={{ display: "flex", alignItems: "center", gap: 10 }}>{handle}<span style={CARD_TITLE}>{t("reports.campusPulse.text43", "What was measured")}</span></span>
        <span style={{ fontSize: 10, color: T.soft }}>{v.nMeasured.toLocaleString()} {t("reports.campusPulse.text27", "of")}{v.directoryTotal === null ? "—" : v.directoryTotal.toLocaleString()}</span>
      </div>
      <div style={{ maxHeight: 320, overflowY: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed" }}>
          <tbody>
            {rows.map((r) => (
              <tr key={r.key}>
                <td style={{ ...TD, padding: "7px 16px", whiteSpace: "normal", color: T.n1, fontSize: 12 }}>{r.label}</td>
                <td style={{ ...TD, padding: "7px 16px", textAlign: "right", width: 130 }}>
                  <span style={{ fontSize: 9.5, fontWeight: 700, padding: "2px 8px", borderRadius: 999, background: TONE[r.verdict].bg, color: TONE[r.verdict].fg, whiteSpace: "nowrap" }}>{VERDICT_LABEL[r.verdict]}</span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {v.coverage.notes.length ? (
        <div style={{ display: "flex", flexDirection: "column", gap: 6, padding: "10px 16px 14px", borderTop: `1px solid ${T.border}` }}>
          {v.coverage.notes.map((n, i) => <NotMeasured key={i} compact why={n} />)}
        </div>
      ) : null}
    </div>
  );
}

function DeptPanel({ v, handle }: { v: View; handle: ReactNode }) {
  const t = useT();
  return (
    <div style={WRAP}>
      <div style={SECTION_HEAD}>
        <span style={{ display: "flex", alignItems: "center", gap: 10 }}>{handle}<span style={CARD_TITLE}>{t("reports.campusPulse.text44", "Departments reached")}</span></span>
        <span style={{ fontSize: 10, color: T.soft }}>{t("reports.campusPulse.text45", "visits · % of students")}</span>
      </div>
      <div style={{ maxHeight: 320, overflowY: "auto", overflowX: "hidden" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed" }}>
          <tbody>
            {v.depts.map((d) => (
              <tr key={d.key}>
                <td style={{ ...TD, padding: "8px 16px", whiteSpace: "normal" }}>
                  <span style={{ display: "flex", alignItems: "center", gap: 7 }}>
                    <span style={{ width: 7, height: 7, borderRadius: 2, background: d.kind === "support" ? SUP_C : d.kind === "growth" ? GRO_C : T.n4, flexShrink: 0 }} />
                    <span style={{ display: "flex", flexDirection: "column" }}>
                      <span style={{ color: T.n1, fontSize: 12 }}>{d.label}</span>
                      {!d.total ? <span style={{ fontSize: 9.5, color: T.soft }}>{t("reports.campusPulse.text46", "no visits in this view")}</span> : null}
                    </span>
                  </span>
                </td>
                <td style={{ ...TD, padding: "8px 16px", textAlign: "right", width: 92, whiteSpace: "normal" }}>
                  <span style={{ display: "flex", flexDirection: "column", alignItems: "flex-end" }}>
                    <span style={{ ...MONO, color: d.total ? T.n1 : T.soft, fontWeight: 600 }}>{d.total.toLocaleString()}</span>
                    <span style={{ ...MONO, fontSize: 9.5, color: T.soft }}>{d.reach === null ? "—" : t("reports.campusPulse.text47", "{reach}%", { reach: d.reach })}</span>
                  </span>
                </td>
              </tr>
            ))}
            {!v.depts.length ? <tr><td style={{ ...TD, padding: "16px" }}><NotMeasured why="No department could be attributed in this scope." /></td></tr> : null}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/**
 * By program. v1.1's program breakdown carries students, interactions and the
 * share of the cohort — not the medians, walk-in share, no-show rate or trend
 * split the client-side model derived per program. Those columns are gone
 * rather than zero-filled.
 */
function ProgramTable({ v, program, onProgram, handle }: { v: View; program: string; onProgram: (id: string) => void; handle: ReactNode }) {
  const t = useT();
  return (
    <div style={WRAP}>
      <div style={SECTION_HEAD}>
        <span style={{ display: "flex", alignItems: "center", gap: 10 }}>{handle}<span style={CARD_TITLE}>{t("reports.campusPulse.text48", "By program")}</span></span>
        {program !== "all" ? <Btn variant="ghost" onClick={() => onProgram("all")}>{t("reports.campusPulse.text49", "Clear filter")}</Btn> : null}
      </div>
      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 520 }}>
          <thead>
            <tr>
              <th style={{ ...TH, minWidth: 190 }}>{t("reports.campusPulse.text50", "Program")}</th>
              <th style={{ ...TH, textAlign: "right" }}>{t("reports.campusPulse.text51", "Students")}</th>
              <th style={{ ...TH, textAlign: "right" }}>{t("reports.campusPulse.text30", "Interactions")}</th>
              <th style={{ ...TH, minWidth: 130 }}>{t("reports.campusPulse.text52", "Share of contact")}</th>
            </tr>
          </thead>
          <tbody>
            {v.programs.map((p) => (
              <tr key={p.key} onClick={() => onProgram(program === p.key ? "all" : p.key)}
                style={{ cursor: "pointer", background: program === p.key ? T.p4 : "transparent" }}>
                <td style={{ ...TD, color: T.n1, fontWeight: 500 }}>{p.label}</td>
                <td style={{ ...TD, ...MONO, textAlign: "right" }}>{int(p.students)}</td>
                <td style={{ ...TD, ...MONO, textAlign: "right", color: T.n1, fontWeight: 600 }}>{p.interactions.toLocaleString()}</td>
                <td style={{ ...TD }}>
                  <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <span style={{ width: 84 }}><Bar height={7} segs={[{ v: p.share ?? 0, c: T.p1, label: t("reports.campusPulse.label2", "Share") }, { v: 1 - (p.share ?? 0), c: "transparent", label: "" }]} /></span>
                    <span style={{ ...MONO, fontSize: 11, color: T.soft }}>{pct(p.share)}</span>
                  </span>
                </td>
              </tr>
            ))}
            {!v.programs.length ? <tr><td colSpan={4} style={{ ...TD, padding: 16 }}><NotMeasured why="No program could be attributed in this scope." /></td></tr> : null}
          </tbody>
        </table>
        <div style={{ padding: "8px 16px 12px" }}>
          <NotMeasured compact why="Per-program medians, walk-in share, no-show rate and the rising/flat/falling split are not part of this report. The same measures for the whole scope are in the KPI band above." />
        </div>
      </div>
    </div>
  );
}

function WhyChip({ label, tone }: { label: string; tone: "warn" | "info" | "danger" | "neutral" }) {
  const map = { warn: { bg: T.orange2, fg: T.orange1 }, info: { bg: T.blue2, fg: T.blue1 }, danger: { bg: T.red2, fg: T.red1 }, neutral: { bg: T.n6, fg: T.n2 } };
  const c = map[tone];
  return <span style={{ fontSize: 10, fontWeight: 700, padding: "2px 7px", borderRadius: 999, background: c.bg, color: c.fg, whiteSpace: "nowrap" }}>{label}</span>;
}

/** The roster is a SERVER page. Paging, sorting and search are request
 *  parameters, so `roster.total` is the number of matches, not the number of
 *  rows in the browser. */
function Roster({ v, onPick, onSort, onPage, handle, activeFinding, assigned, onAssignRow, canExport, onExport, busyRow, scopeNote }: {
  v: View;
  onPick: (id: string) => void; onSort: (id: CampusPulseRosterSortKey) => void; onPage: (p: number) => void;
  handle: ReactNode;
  activeFinding: Finding | null;
  assigned: Record<string, { action: string; owner: string; at: number }>;
  onAssignRow: (s: Student) => void;
  canExport: boolean; onExport: () => void; busyRow: string | null;
  scopeNote: string | null;
}) {
  const t = useT();
  const r = v.roster;
  const rows = r.rows;
  const pages = Math.max(1, Math.ceil(r.total / r.limit));
  const p = r.page - 1;
  const from = r.total ? p * r.limit + 1 : 0;
  const to = Math.min(r.total, p * r.limit + rows.length);
  const maxTerm = Math.max(1, ...rows.map((s) => Math.max(0, ...s.termCounts)));
  const verb = activeFinding?.verb ?? null;
  const verbBlocked = activeFinding?.verbBlocked ?? null;

  return (
    <div style={WRAP}>
      <div style={{ ...SECTION_HEAD, flexWrap: "wrap" }}>
        <span style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
          {handle}<span style={CARD_TITLE}>{t("reports.campusPulse.text53", "Student roster")}</span>
          {scopeNote ? (
            <span style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 11, fontWeight: 700, color: T.p1, background: T.p4, borderRadius: 999, padding: "3px 10px" }}>{scopeNote}</span>
          ) : null}
        </span>
        {canExport
          ? <Btn variant="secondary" icon={<Icon.csv size={14} />} onClick={onExport}>{t("reports.campusPulse.text54", "Export CSV · gated")}</Btn>
          : <span style={{ fontSize: 11, color: T.soft }}>{t("reports.campusPulse.text55", "Export needs the report-export capability")}</span>}
      </div>
      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 1180 }}>
          <thead>
            <tr>
              <th style={{ ...TH, width: 52 }} title={t("reports.campusPulse.title17", "Ranked intervention priority — on the long-range roadmap (spec 13.1). The column is here so its arrival is not a redesign.")}>{t("reports.campusPulse.text56", "Rank")}</th>
              <SortTh id="displayName" label={t("reports.campusPulse.label6", "Student")} sort={v.roster.sort} dir={v.roster.direction} onSort={onSort} width={190} />
              <th style={{ ...TH, minWidth: 200 }} title={t("reports.campusPulse.title18", "The factors behind the flag — never a score")}>{t("reports.campusPulse.text57", "Why flagged")}</th>
              <SortTh id="program" label={t("reports.campusPulse.label7", "Program")} sort={v.roster.sort} dir={v.roster.direction} onSort={onSort} width={160} />
              <th style={{ ...TH, width: 82 }}>{t("reports.campusPulse.text58", "By term")}</th>
              <SortTh id="interactions" label={t("reports.campusPulse.label", "Interactions")} sort={v.roster.sort} dir={v.roster.direction} onSort={onSort} align="right" />
              <SortTh id="departmentsTouched" label={t("reports.campusPulse.label8", "Depts")} sort={v.roster.sort} dir={v.roster.direction} onSort={onSort} align="right" title={t("reports.campusPulse.title19", "Distinct departments this student's interactions touched")} />
              <SortTh id="engagementTrend" label={t("reports.campusPulse.label9", "Trend")} sort={v.roster.sort} dir={v.roster.direction} onSort={onSort} align="right" title={t("reports.campusPulse.title20", "Least-squares slope of interactions per term, over complete terms only")} />
              <SortTh id="walkInShare" label={t("reports.campusPulse.label10", "Walk-in")} sort={v.roster.sort} dir={v.roster.direction} onSort={onSort} align="right" title={t("reports.campusPulse.title21", "Walk-ins ÷ (walk-ins + appointments)")} />
              <SortTh id="noShowCount" label={t("reports.campusPulse.label11", "No-shows")} sort={v.roster.sort} dir={v.roster.direction} onSort={onSort} align="right" />
              <th style={{ ...TH, width: 132, textAlign: "right" }}>{t("reports.campusPulse.text59", "Status")}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((s) => {
              const why = whyFlagged(s, v);
              const done = assigned[s.customerId];
              return (
                <tr key={s.customerId} style={{ cursor: "pointer" }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = T.n6; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}>
                  <td style={{ ...TD, ...MONO, color: T.n5, textAlign: "center" }} onClick={() => onPick(s.customerId)}>—</td>
                  <td style={TD} onClick={() => onPick(s.customerId)}>
                    <span style={{ display: "flex", flexDirection: "column", gap: 1 }}>
                      <span style={{ color: T.n1, fontWeight: 500 }}>{s.displayName ?? t("reports.campusPulse.text60", "Name withheld")}</span>
                      <span style={{ ...MONO, fontSize: 10.5, color: T.soft }}>{s.customerId}</span>
                    </span>
                  </td>
                  <td style={{ ...TD, whiteSpace: "normal" }} onClick={() => onPick(s.customerId)}>
                    {why.length
                      ? <span style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>{why.map((w) => <WhyChip key={w.label} label={w.label} tone={w.tone} />)}</span>
                      : <span style={{ fontSize: 11, color: T.soft }}>{t("reports.campusPulse.text61", "nothing flagged")}</span>}
                  </td>
                  <td style={TD} onClick={() => onPick(s.customerId)}>
                    <span style={{ display: "flex", flexDirection: "column", gap: 1 }}>
                      <span>{s.program ?? "—"}</span>
                      <span style={{ fontSize: 10.5, color: T.soft }}>{s.advisorName ?? "—"}</span>
                    </span>
                  </td>
                  <td style={TD} onClick={() => onPick(s.customerId)}><Spark values={s.termCounts} max={maxTerm} /></td>
                  <td style={{ ...TD, ...MONO, textAlign: "right", color: T.n1, fontWeight: 600 }} onClick={() => onPick(s.customerId)}>{s.interactions}</td>
                  <td style={{ ...TD, ...MONO, textAlign: "right" }} onClick={() => onPick(s.customerId)}>{s.departmentsTouched}</td>
                  <td style={{ ...TD, textAlign: "right" }} onClick={() => onPick(s.customerId)}>
                    <span style={{ display: "flex", justifyContent: "flex-end" }}><TrendCell slope={s.engagementTrend} /></span>
                  </td>
                  <td style={{ ...TD, ...MONO, textAlign: "right" }} onClick={() => onPick(s.customerId)}>{pct(s.walkInShare)}</td>
                  <td style={{ ...TD, ...MONO, textAlign: "right", color: s.noShowCount ? T.red1 : "inherit" }} onClick={() => onPick(s.customerId)}>{s.noShowCount || "—"}</td>
                  <td style={{ ...TD, textAlign: "right" }}>
                    {done ? (
                      <span style={{ display: "inline-flex", flexDirection: "column", alignItems: "flex-end", gap: 1 }}>
                        <span style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: 11, fontWeight: 700, color: T.green1 }}><Icon.check size={11} /> {t("reports.campusPulse.text62", "Recorded")}</span>
                        <span style={{ fontSize: 9.5, color: T.soft, maxWidth: 124, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{done.owner}</span>
                      </span>
                    ) : verb && !verbBlocked ? (
                      <Btn variant="secondary" disabled={busyRow === s.customerId} onClick={() => onAssignRow(s)}>{busyRow === s.customerId ? "…" : t("reports.campusPulse.text63", "Assign")}</Btn>
                    ) : verb && verbBlocked ? (
                      <span style={{ fontSize: 10.5, color: T.soft }} title={verbBlocked}>{t("reports.campusPulse.text64", "unavailable")}</span>
                    ) : (
                      <span style={{ fontSize: 10.5, color: T.soft }}>{t("reports.campusPulse.text65", "select a finding")}</span>
                    )}
                  </td>
                </tr>
              );
            })}
            {!rows.length ? <tr><td colSpan={11} style={{ ...TD, textAlign: "center", padding: 34, color: T.soft }}>{t("reports.campusPulse.text66", "No students match these filters.")}</td></tr> : null}
          </tbody>
        </table>
      </div>
      <div style={{ display: "flex", alignItems: "center", padding: "10px 16px", borderTop: `1px solid ${T.border}`, justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
        <span style={{ ...MONO, fontSize: 11, color: T.soft }}>
          {r.total ? t("reports.campusPulse.text67", "{from}–{to} of {arg3}", { from, to, arg3: r.total.toLocaleString() }) : "0"}
          <span style={{ marginLeft: 10 }}>{t("reports.campusPulse.text68", "Rank fills when scoring lands (roadmap); the column costs nothing today.")}</span>
        </span>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <Btn variant="secondary" disabled={p === 0} onClick={() => onPage(p - 1)}>{t("reports.campusPulse.text69", "Previous")}</Btn>
          <span style={{ ...MONO, fontSize: 11.5, color: T.n2, minWidth: 70, textAlign: "center" }}>{p + 1} / {pages}</span>
          <Btn variant="secondary" disabled={p >= pages - 1} onClick={() => onPage(p + 1)}>{t("reports.campusPulse.text70", "Next")}</Btn>
        </div>
      </div>
    </div>
  );
}

/* ── One student ───────────────────────────────────────────────────── */
function PeerStat({ label, value, unit, peer, peerLabel, hint }: { label: string; value: ReactNode; unit?: string; peer?: number | null; peerLabel?: string; hint?: string }) {
  const t = useT();
  const n = typeof value === "number" ? value : parseFloat(String(value));
  const d = peer === null || peer === undefined || isNaN(n) ? null : Math.round((n - peer) * 10) / 10;
  const tone = d === null || Math.abs(d) < 0.05 ? T.soft : d > 0 ? T.green1 : T.red1;
  return (
    <Card style={{ padding: "12px 14px", display: "flex", flexDirection: "column", gap: 4, minWidth: 0 }}>
      <div style={{ ...EYEBROW, whiteSpace: "normal" }}>{label}</div>
      <div style={{ display: "flex", alignItems: "baseline", gap: 4 }}>
        <span style={{ ...MONO, fontSize: 23, fontWeight: 800, color: T.n1 }}>{value}</span>
        {unit ? <span style={{ fontSize: 11.5, color: T.soft, fontWeight: 600 }}>{unit}</span> : null}
      </div>
      {peer !== null && peer !== undefined ? (
        <div style={{ display: "flex", gap: 6, fontSize: 10.5 }}>
          <span style={{ color: T.soft }}>{peerLabel || t("reports.campusPulse.text71", "program median")} {num(peer)}</span>
          <span style={{ ...MONO, color: tone, fontWeight: 600 }}>{d !== null && d > 0 ? "+" : ""}{d === null ? "—" : d}</span>
        </div>
      ) : null}
      {hint ? <div style={{ fontSize: 10, color: T.soft, lineHeight: 1.35 }}>{hint}</div> : null}
    </Card>
  );
}

/** One student's record across the configured terms, from the roster row's own
 *  `termCounts` — index-for-index with `report.terms`. */
function StudentLadder({ v, s }: { v: View; s: Student }) {
  const t = useT();
  const terms = v.report.terms;
  const max = Math.max(1, ...s.termCounts);
  return (
    <div style={WRAP}>
      <div style={SECTION_HEAD}>
        <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <span style={CARD_TITLE}>{t("reports.campusPulse.text72", "Record by term")}</span>
          <span style={{ fontSize: 11, color: T.soft }}>{v.completeTerms} {t("reports.campusPulse.text27", "of")}{v.totalTerms} {t("reports.campusPulse.text28", "terms complete")}</span>
        </div>
      </div>
      {!terms.length || !s.termCounts.length ? (
        <div style={{ padding: "16px 18px" }}>
          <NotMeasured why="No academic terms are configured for this tenant, so this student's interactions cannot be placed on a ladder." />
        </div>
      ) : (
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 420 }}>
            <thead>
              <tr>
                <th style={{ ...TH, width: 220 }}>{t("reports.campusPulse.text29", "Term")}</th>
                <th style={{ ...TH, minWidth: 140 }}>{t("reports.campusPulse.text30", "Interactions")}</th>
                <th style={{ ...TH, textAlign: "right", width: 80 }}>{t("reports.campusPulse.text31", "Total")}</th>
              </tr>
            </thead>
            <tbody>
              {terms.map((term, i) => {
                const total = s.termCounts[i] ?? 0;
                const row = v.ladder[i];
                return (
                  <tr key={term.key} style={{ background: row && !row.complete ? T.n6 : "transparent" }}>
                    <td style={{ ...TD, color: T.n1, fontWeight: 500 }}>
                      <span style={{ display: "flex", alignItems: "center", gap: 7 }}>
                        <span>{term.label}</span>
                        {row && !row.complete ? <span style={{ fontSize: 9, fontWeight: 700, color: T.soft, background: T.n6, border: `1px solid ${T.border}`, borderRadius: 999, padding: "1px 6px" }}>{row.started ? t("reports.campusPulse.text35", "in progress") : t("reports.campusPulse.text36", "not started")}</span> : null}
                      </span>
                    </td>
                    <td style={TD}>
                      {total > 0
                        ? <div style={{ width: `${Math.max(16, (total / max) * 100)}%` }}><Bar height={9} segs={[{ v: total, c: LANE_C.appt, label: t("reports.campusPulse.label", "Interactions") }]} /></div>
                        : <span style={{ fontSize: 11, color: T.soft }}>{row && row.complete ? t("reports.campusPulse.text73", "no interactions") : "—"}</span>}
                    </td>
                    <td style={{ ...TD, ...MONO, textAlign: "right", color: T.n1, fontWeight: 600 }}>{total || "—"}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <div style={{ padding: "8px 16px 12px" }}>
            <NotMeasured compact why="Individual visits are not part of this report: it returns per-term counts and the measures below, not a list of interactions. Nothing here is a sample of a longer list." />
          </div>
        </div>
      )}
    </div>
  );
}

function StudentView({ v, s, onBack, cols }: { v: View; s: Student; onBack: () => void; cols: number }) {
  const t = useT();
  const why = whyFlagged(s, v);
  const facts: { k: string; v: string; sub?: string | null }[] = [
    { k: "Program", v: s.program ?? "—" },
    { k: "Campus of record", v: s.campusOfRecord ?? "—" },
    { k: "Assigned advisor", v: s.advisorName ?? (v.visibility.staffNames ? "—" : "Withheld for your role") },
    { k: "Class year", v: s.classYear === null ? "—" : String(s.classYear), sub: v.cohortFormed ? v.cohortLabel : "not a formed class cohort" },
    { k: "Most recent interaction", v: s.lastSeenAt ? dLong(s.lastSeenAt) : "none recorded" },
    { k: "Repeat interactions", v: String(s.repeatCount) },
  ];
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
        <Btn variant="ghost" onClick={onBack}>{t("reports.campusPulse.text74", "← Back to the roster")}</Btn>
        <span style={{ display: "flex", flexDirection: "column" }}>
          <span style={{ fontSize: 15, fontWeight: 700, color: T.n1 }}>{s.displayName ?? t("reports.campusPulse.text60", "Name withheld")}</span>
          <span style={{ ...MONO, fontSize: 11, color: T.soft }}>{s.customerId}</span>
        </span>
        {why.length ? <span style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>{why.map((w) => <WhyChip key={w.label} label={w.label} tone={w.tone} />)}</span> : null}
      </div>

      <div style={WRAP}>
        <div style={SECTION_HEAD}><span style={CARD_TITLE}>{t("reports.campusPulse.text75", "Record")}</span></div>
        <div style={{ display: "grid", gridTemplateColumns: `repeat(${Math.min(3, cols)}, minmax(0, 1fr))`, gap: 0 }}>
          {facts.map((x) => (
            <div key={x.k} style={{ display: "flex", flexDirection: "column", gap: 2, padding: "11px 16px", borderBottom: `1px solid ${T.n6}`, minWidth: 0 }}>
              <span style={EYEBROW}>{x.k}</span>
              <span style={{ fontSize: 12.5, color: T.n1, ...PRETTY }}>{x.v}</span>
              {x.sub ? <span style={{ fontSize: 10.5, color: T.soft, ...PRETTY }}>{x.sub}</span> : null}
            </div>
          ))}
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: `repeat(${Math.min(3, cols)}, minmax(0, 1fr))`, gap: 12 }}>
        <PeerStat label={t("reports.campusPulse.label", "Interactions")} value={s.interactions}
          peer={s.peer ? s.peer.programMedianInteractions : null} peerLabel="program median"
          hint={s.peer ? t("reports.campusPulse.hint", "Cohort median {arg1} · ranked in a group of {groupSize}", { arg1: num(s.peer.cohortMedianInteractions), groupSize: s.peer.groupSize }) : undefined} />
        <PeerStat label={t("reports.campusPulse.label12", "Percentile in class year and program")} value={s.peer && s.peer.percentileInCohort !== null ? Math.round(s.peer.percentileInCohort * 100) : "—"} unit={s.peer && s.peer.percentileInCohort !== null ? "%" : undefined}
          hint={t("reports.campusPulse.hint2", "Against the window's full measured population, ignoring every filter and scope — so this number never moves when you narrow the report.")} />
        <PeerStat label={t("reports.campusPulse.label13", "Departments touched")} value={s.departmentsTouched} />
        <PeerStat label={t("reports.campusPulse.label14", "Walk-in share")} value={s.walkInShare === null ? "—" : Math.round(s.walkInShare * 100)} unit={s.walkInShare === null ? undefined : "%"} />
        <PeerStat label={t("reports.campusPulse.label11", "No-shows")} value={s.noShowCount} hint={t("reports.campusPulse.hint3", "{cancelCount} canceled", { cancelCount: s.cancelCount })} />
        <PeerStat label={t("reports.campusPulse.label15", "Silent terms in the lookback")} value={s.darkTermsInLookback === null ? "—" : s.darkTermsInLookback}
          hint={s.darkTermsInLookback === null ? t("reports.campusPulse.hint4", "No term has completed yet.") : t("reports.campusPulse.hint5", "Of the last three completed terms.")} />
      </div>

      {!s.peer ? (
        <Card style={{ padding: "12px 15px" }}>
          <NotMeasured why="No peer comparison for this student: the report returns one only when the student carries both a class year and a program and the fixed reference population could be read. coverage notes above say which." />
        </Card>
      ) : null}

      <StudentLadder v={v} s={s} />
    </div>
  );
}

/* ── Screen ────────────────────────────────────────────────────────── */
const SPANS: Record<string, number> = { semester: 3, programs: 3, types: 1, coverage: 1, depts: 1, roster: 3 };
const DEF_ORDER = ["semester", "programs", "types", "coverage", "depts", "roster"];

export default function ReportsCampusPulse() {
  const t = useT();
  const nav = useNav();
  const tenant = useTenant();
  const user = useUser();

  /* ── Who may see what ─────────────────────────────────────────────────
   * Names are the SERVER's decision: `visibility.studentNames` /
   * `visibility.staffNames` are computed from the caller's capabilities and the
   * contract says they are never asserted by the client. The capability checks
   * below gate what this screen may DO — act and export — not what it may see. */
  const canAct = isAdmin() || can("campaigns.send") || can("campaigns.send_individual");
  const canExportCap = isAdmin() || can("reports.export");

  const [classYear, setClassYear] = useState<number | null>(null);
  const [sel, setSel] = useState<string | null>(null);
  const [f, setF] = useState<Filters>(DEFAULT_FILTERS);
  const [page, setPage] = useState(0);
  const [scope, setScope] = useState<Scope>(null);
  const [draft, setDraft] = useState("");
  useEffect(() => { const h = setTimeout(() => setF((x) => ({ ...x, search: draft })), 200); return () => clearTimeout(h); }, [draft]);
  useEffect(() => { setSel(null); }, [classYear, scope]);
  useEffect(() => { setPage(0); }, [classYear, scope, f.program, f.campus, f.search, f.sort, f.dir, f.lanes]);

  /* Action state — the loop has to visibly close, so both the per-card and the
   * per-student hand-offs survive a reload. */
  const [verbStates, setVerbStates] = useState<Record<string, { state: "done"; n: number; at: number }>>(() => {
    try { return JSON.parse(window.localStorage.getItem("cp-card-actions") || "{}"); } catch { return {}; }
  });
  const [assigned, setAssigned] = useState<Record<string, { action: string; owner: string; at: number }>>(() => {
    try { return JSON.parse(window.localStorage.getItem("cp-assignments") || "{}"); } catch { return {}; }
  });
  useEffect(() => { try { window.localStorage.setItem("cp-card-actions", JSON.stringify(verbStates)); } catch { /* ignore */ } }, [verbStates]);
  useEffect(() => { try { window.localStorage.setItem("cp-assignments", JSON.stringify(assigned)); } catch { /* ignore */ } }, [assigned]);
  const [busyVerb, setBusyVerb] = useState<string | null>(null);
  const [busyRow, setBusyRow] = useState<string | null>(null);
  const [confirmVerb, setConfirmVerb] = useState<Finding | null>(null);
  const [exportOpen, setExportOpen] = useState(false);
  const [purpose, setPurpose] = useState("");
  const [purposeErr, setPurposeErr] = useState(false);

  const [cols, setCols] = useState(3);
  const rootRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const el = rootRef.current; if (!el || typeof ResizeObserver === "undefined") return;
    const ro = new ResizeObserver(() => { const w = el.clientWidth; setCols(w < 720 ? 1 : w < 1080 ? 2 : 3); });
    ro.observe(el); return () => ro.disconnect();
  }, []);

  const [order, setOrder] = useState<string[]>(() => {
    try {
      const raw = JSON.parse(window.localStorage.getItem("cp-layout-order") || "null");
      if (Array.isArray(raw) && raw.length === DEF_ORDER.length && DEF_ORDER.every((id) => raw.includes(id))) return raw;
    } catch { /* ignore */ }
    return DEF_ORDER;
  });
  useEffect(() => { try { window.localStorage.setItem("cp-layout-order", JSON.stringify(order)); } catch { /* ignore */ } }, [order]);
  const [armed, setArmed] = useState<string | null>(null);
  const [dragId, setDragId] = useState<string | null>(null);
  const [overId, setOverId] = useState<string | null>(null);
  const moveSection = (from: string | null, to: string) => setOrder((o) => {
    if (!from || from === to) return o;
    const a = o.slice(), i = a.indexOf(from), j = a.indexOf(to);
    if (i < 0 || j < 0) return o;
    a.splice(j, 0, a.splice(i, 1)[0]);
    return a;
  });

  /* One request per state change. The scope, the filters, the roster page, the
   * sort and the search are ALL parameters, so what comes back already
   * describes the set on screen — nothing is narrowed in the browser. */
  const params = useMemo(() => toParameters(f, classYear, page, scope), [f, classYear, page, scope]);
  const reqOptions = useMemo(() => studioRequestOptions("campus-pulse"), []);
  const { data: report, loading, error, reload } = useWfResource<CampusPulseReport>(
    `campus-pulse|${JSON.stringify(params)}`,
    async () => (await getCampusPulseReport(params, reqOptions)).data,
  );

  const cohortKey = useMemo(() => {
    if (!report) return "";
    const hit = classYear === null ? null : report.cohorts.find((c) => c.classYear === classYear);
    return hit ? hit.key : (report.cohorts[0]?.key ?? "all-students");
  }, [report, classYear]);

  const v: View | null = useMemo(() => (report ? buildView(report, cohortKey) : null), [report, cohortKey]);
  const staffNamesVisible = !!v?.visibility.staffNames;
  const namesVisible = !!v?.visibility.studentNames;
  const canExport = namesVisible && canExportCap;
  const fnds: Finding[] = useMemo(() => (v ? buildFindings(v, staffNamesVisible).map((finding) =>
    finding.verb?.kind === "handoff" ? { ...finding, verbBlocked: HANDOFF_UNAVAILABLE } : finding
  ) : []), [v, staffNamesVisible]);

  const activeQuad = scope && "quadrant" in scope ? scope.quadrant : null;
  const activeFinding = useMemo(
    () => (scope && "finding" in scope ? fnds.find((x) => x.key === scope.finding) ?? null : null),
    [scope, fnds],
  );
  const selected = useMemo(() => (v && sel ? v.roster.rows.find((s) => s.customerId === sel) ?? null : null), [v, sel]);

  const onSort = useCallback((id: CampusPulseRosterSortKey) => setF((x) => ({
    ...x, sort: id,
    dir: x.sort === id ? (x.dir === "asc" ? "desc" : "asc") : id === "displayName" || id === "program" ? "asc" : "desc",
  })), []);
  const toggleLane = (id: Lane) => setF((x) => {
    const next = { ...x.lanes, [id]: !x.lanes[id] };
    return Object.values(next).some(Boolean) ? { ...x, lanes: next } : x;
  });
  const anyFilter = f.program !== "all" || f.campus !== "all" || !!f.search || LANES.some((l) => !f.lanes[l.id]);

  /** The members of a finding are a SERVER scope, not a list this screen holds.
   *  To act on all of them the report is re-read scoped to that finding with a
   *  roster page big enough to carry every member, and the action is refused
   *  outright if it would not — a partial batch under a total is worse than no
   *  batch at all. */
  const membersOf = async (fd: Finding): Promise<Student[]> => {
    if (!fd.scope) return [];
    const probe = await getCampusPulseReport(toParameters(f, classYear, 0, fd.scope, ROSTER_PAGE), reqOptions);
    const total = probe.data.roster.total;
    const cap = probe.data.coverage.limits.studentRowCap;
    if (total <= probe.data.roster.rows.length) return probe.data.roster.rows;
    if (total > cap) {
      throw new Error(`This finding names ${total.toLocaleString()} students and the report will return at most ${cap.toLocaleString()} in one page, so the batch cannot be completed in full.`);
    }
    const full = await getCampusPulseReport(toParameters(f, classYear, 0, fd.scope, total), reqOptions);
    if (full.data.roster.rows.length < full.data.roster.total) {
      throw new Error(`Only ${full.data.roster.rows.length} of ${full.data.roster.total} members came back, so the batch was not started.`);
    }
    return full.data.roster.rows;
  };

  /** One verb, awaited against a real op, then the state that closes the loop. */
  const runVerb = async (fd: Finding, only?: Student) => {
    const verb = fd.verb;
    if (!verb) return;
    // A hand-off requires an owning operation; a local audit entry is not one.
    if (verb.kind === "handoff") {
      toast({ tone: "warn", title: t("reports.campusPulse.title", "Not available yet"), subtitle: HANDOFF_UNAVAILABLE });
      return;
    }
    if (fd.verbBlocked) {
      toast({ tone: "warn", title: t("reports.campusPulse.title", "Not available yet"), subtitle: fd.verbBlocked });
      return;
    }
    if (only) setBusyRow(only.customerId); else setBusyVerb(fd.id);
    try {
      const list = only ? [only] : await membersOf(fd);
      if (!list.length) {
        toast({ tone: "warn", title: t("reports.campusPulse.title2", "Nothing to do"), subtitle: t("reports.campusPulse.subtitle", "This finding names no students to act on.") });
        return;
      }
      let ok = 0;
      const failed: string[] = [];
      for (let i = 0; i < list.length; i += 8) {
        const batch = list.slice(i, i + 8);
        const results = await Promise.allSettled(batch.map(async (s) => {
          await customerItemAction(s.customerId, { kind: "advising", action: verb.id, ref: s.advisorName ?? "" });
        }));
        results.forEach((r, n) => { if (r.status === "fulfilled") ok++; else failed.push(batch[n].displayName ?? batch[n].customerId); });
        const done = batch.filter((_, n) => results[n].status === "fulfilled");
        if (done.length) {
          setAssigned((x) => {
            const next = { ...x };
            done.forEach((s) => { next[s.customerId] = { action: verb.id, owner: fd.owner.replace(/\.$/, ""), at: Date.now() }; });
            return next;
          });
        }
      }
      logAction("guest", `campus-pulse:${fd.id}`, `Campus Pulse · ${fd.headline}`, verb.id, {
        changes: [
          { field: "recipients", from: null, to: ok },
          { field: "failed", from: null, to: failed.length },
          { field: "owner", from: null, to: fd.owner },
        ],
      });
      if (!only) setVerbStates((x) => ({ ...x, [fd.id]: { state: "done", n: ok, at: Date.now() } }));
      toast({
        tone: failed.length ? "warn" : "success",
        title: failed.length ? `${ok} of ${list.length} handed over` : verb.done,
        subtitle: failed.length ? `Failed: ${failed.slice(0, 3).join(", ")}${failed.length > 3 ? "…" : ""}` : `${plural(ok, "student")} · ${fd.owner}`,
      });
    } catch (e) {
      toast({ tone: "danger", title: t("reports.campusPulse.title3", "Nothing was recorded"), subtitle: e instanceof Error ? e.message : String(e) });
    } finally {
      setBusyVerb(null); setBusyRow(null);
    }
  };

  const scopeLabel = useMemo(() => {
    if (!scope) return null;
    if ("quadrant" in scope) return QUADRANT_LABEL[scope.quadrant];
    return activeFinding ? `${activeFinding.value} ${activeFinding.headline}` : "Selected finding";
  }, [scope, activeFinding]);

  /** Gated, logged, stamped. The file names its own scope, its purpose and the
   *  person accountable for it; the audit trail keeps the same record. The
   *  roster is server-paged, so the export re-reads the whole scoped roster
   *  rather than writing out whichever page happens to be on screen. */
  const doExport = async () => {
    if (!v || !purpose.trim()) { setPurposeErr(true); return; }
    const esc = (val: unknown) => `"${String(val == null ? "" : val).replace(/"/g, '""')}"`;
    try {
      const total = v.roster.total;
      const cap = v.coverage.limits.studentRowCap;
      if (total > cap) {
        toast({ tone: "danger", title: t("reports.campusPulse.title4", "Nothing was exported"), subtitle: t("reports.campusPulse.exportTooLarge", "This scope has {total} rows and the report returns at most {cap} at once. Narrow it and export again.", { total: total.toLocaleString(), cap: cap.toLocaleString() }) });
        return;
      }
      const full = await getCampusPulseReport(toParameters(f, classYear, 0, scope, Math.max(total, 1)), reqOptions);
      const rows = full.data.roster.rows;
      if (rows.length < full.data.roster.total) {
        toast({ tone: "danger", title: t("reports.campusPulse.title4", "Nothing was exported"), subtitle: t("reports.campusPulse.exportPartial", "Only {count} of {total} rows came back — a partial file would not say so.", { count: rows.length, total: full.data.roster.total }) });
        return;
      }
      const scopeLine = [
        v.cohortLabel,
        f.program === "all" ? "all programs" : f.program,
        scopeLabel ? `scope: ${scopeLabel}` : null,
        f.search ? `search: ${f.search}` : null,
      ].filter(Boolean).join(" · ");
      const when = new Date().toISOString();
      const stamp = [
        `# Campus Pulse — ${tenant?.name || "student roster"}`,
        `# Scope: ${scopeLine}`,
        `# Exported by: ${user.name || "unknown"} <${user.email || "—"}>`,
        `# Exported at: ${when}`,
        `# Purpose: ${purpose.trim().replace(/[\r\n]+/g, " ")}`,
        `# Rows: ${rows.length} — named students and record ids; handle under the student-records policy`,
      ];
      const head = ["Student", "ID", "Program", "Campus", "Advisor", "Why flagged", "Interactions", "Departments", "Trend/term", "Walk-in share %", "No-shows", "Assigned action", "Assigned owner", "Assigned at"];
      const lines = stamp.concat([head.join(",")]).concat(rows.map((s) => {
        const a = assigned[s.customerId];
        const why = whyFlagged(s, v).map((w) => w.label).join("; ");
        return [
          esc(s.displayName ?? ""), esc(s.customerId), esc(s.program ?? ""), esc(s.campusOfRecord ?? ""), esc(s.advisorName ?? ""), esc(why),
          s.interactions, s.departmentsTouched, s.engagementTrend === null ? "" : num(s.engagementTrend),
          s.walkInShare === null ? "" : Math.round(s.walkInShare * 100), s.noShowCount,
          esc(a ? a.action : ""), esc(a ? a.owner : ""), esc(a ? new Date(a.at).toISOString() : ""),
        ].join(",");
      }));
      const url = URL.createObjectURL(new Blob([lines.join("\n")], { type: "text/csv" }));
      const el = document.createElement("a");
      el.href = url; el.download = `campus-pulse-${v.cohortKey}-${when.slice(0, 10)}.csv`;
      document.body.appendChild(el); el.click(); el.remove();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
      logAction("guest", `campus-pulse-export:${v.cohortKey}`, "Campus Pulse roster export", "exported", {
        changes: [
          { field: "rows", from: null, to: rows.length },
          { field: "scope", from: null, to: scopeLine },
          { field: "purpose", from: null, to: purpose.trim() },
        ],
      });
      toast({ tone: "success", title: t("reports.campusPulse.title5", "Export logged"), subtitle: t("reports.campusPulse.exportRecorded", "{count, plural, one {# row} other {# rows}} · recorded in the audit trail", { count: rows.length }) });
      setExportOpen(false); setPurpose(""); setPurposeErr(false);
    } catch (e) {
      toast({ tone: "danger", title: t("reports.campusPulse.title4", "Nothing was exported"), subtitle: e instanceof Error ? e.message : String(e) });
    }
  };

  const header = (
    <PageHeader
      title={t("reports.campusPulse.campusPulse", "Campus Pulse")}
      /* `false`, not absent: PageHeader's guard is `breadcrumb !== null &&
         breadcrumb !== undefined ? breadcrumb : <Dashboard link>`, so omitting
         it falls through to the default "← Dashboard" (chrome.tsx:62). The tab
         strip is the way back from this report. */
      breadcrumb={false}
      subtitle={v
        ? `${t("reports.campusPulse.measuredSubtitle", "{cohort} · {measured} of {total} students measured · {interactions} interactions", { cohort: v.cohortLabel, measured: v.nMeasured.toLocaleString(), total: v.directoryTotal === null ? "—" : v.directoryTotal.toLocaleString(), interactions: v.interactions.toLocaleString() })}${v.cohortFormed ? "" : ` · ${t("reports.campusPulse.noClassYear", "no class year could be formed")}`}`
        : undefined}
      actions={v ? (
        <>
          <Select value={classYear === null ? "all" : String(classYear)}
            onChange={(val) => setClassYear(val === "all" ? null : Number(val))}
            minWidth={150} searchable searchHint="Class year…"
            options={[
              { id: "all", label: t("reports.campusPulse.label3", "All cohorts"), meta: `${v.cohorts.length} in the window` },
              ...v.cohorts.filter((c) => c.classYear !== null).map((c) => ({
                id: String(c.classYear), label: c.label, meta: plural(c.size, "student"),
              })),
            ]} />
          <Select value={f.program} onChange={(val) => setF({ ...f, program: val })} minWidth={168} searchable searchHint="Program name…"
            options={[{ id: "all", label: t("reports.campusPulse.label4", "All programs"), meta: `${v.n} students` }, ...v.programs.map((p) => ({ id: p.key, label: p.label, meta: p.students === null ? "" : plural(p.students, "student") }))]} />
          <Select value={f.campus} onChange={(val) => setF({ ...f, campus: val })} minWidth={150}
            disabled
            disabledHint="This report returns no campus breakdown, so there is no list of campuses to filter by. The campus each student is on is on their row in the roster."
            options={[{ id: "all", label: t("reports.campusPulse.label5", "All campuses") }]} />
        </>
      ) : undefined}
    />
  );

  return (
    <Screen rail="reports" tabs={reportTabs(nav, "pulse")} activeTab="pulse">
      {header}
      <DataSourceStrip screen="reports.pulse" ops={OPS} />
      <div ref={rootRef} style={{ padding: "16px 24px 56px", display: "flex", flexDirection: "column", gap: 16, minWidth: 0 }}>
        {error ? <WfPending screen="reports.pulse" opName="getCampusPulseReport" error={error} onRetry={reload} />
          : loading && !v ? <div style={{ padding: 40, textAlign: "center", color: T.soft, fontSize: 14 }}>{t("reports.campusPulse.text76", "Loading the cohort…")}</div>
          : !v ? null
          : !v.nMeasured ? (
            <Card style={{ padding: "18px 20px", display: "flex", flexDirection: "column", gap: 6 }}>
              <span style={CARD_TITLE}>{t("reports.campusPulse.text77", "No students measured")}</span>
              <span style={{ fontSize: 12.5, color: T.n2 }}>{t("reports.campusPulse.text78", "This report measured no students in the window. The coverage block says what it could not read.")}</span>
              {v.coverage.notes.map((n, i) => <NotMeasured key={i} why={n} />)}
            </Card>
          ) : (
            <>
              <div style={{ display: "flex", padding: "10px 14px", gap: 12, flexWrap: "wrap", justifyContent: "space-between", alignItems: "center", background: T.white, border: `1px solid ${T.border}`, borderRadius: 10, position: "relative", zIndex: 60 }}>
                <div style={{ display: "flex", gap: 7, flexWrap: "wrap" }}>
                  {LANES.map((l) => (
                    <Chip key={l.id} on={f.lanes[l.id]} onClick={() => toggleLane(l.id)}>
                      <span style={{ width: 7, height: 7, borderRadius: 2, background: f.lanes[l.id] ? T.onColor : LANE_C[l.id], flexShrink: 0 }} />
                      {l.label}
                      {l.id === "noshow" ? null : <span style={{ ...MONO, fontSize: 10.5, opacity: 0.75 }}>{v.laneCount[l.id].toLocaleString()}</span>}
                    </Chip>
                  ))}
                </div>
                <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
                  <span style={{ fontSize: 10.5, color: T.soft }}>{t("reports.campusPulse.text79", "Viewing as")}<strong style={{ color: T.n1 }}>{user.role || "—"}</strong></span>
                  <div style={{ display: "flex", alignItems: "center", height: 30, borderRadius: 999, border: `1px solid ${T.border}`, background: T.white, padding: "0 10px", gap: 6 }}>
                    <span style={{ color: T.soft, display: "inline-flex" }}><Icon.search size={12} /></span>
                    <input value={draft} onChange={(e) => setDraft(e.target.value)} placeholder={t("reports.campusPulse.placeholder2", "Search name, ID, program, advisor…")}
                      style={{ border: 0, outline: "none", background: "transparent", fontFamily: "inherit", fontSize: 12, color: T.n1, width: 200 }} />
                    {draft ? <button onClick={() => setDraft("")} title={t("reports.campusPulse.title22", "Clear")} style={{ border: 0, background: "transparent", cursor: "pointer", color: T.soft, display: "inline-flex", padding: 0 }}><Icon.x size={11} /></button> : null}
                  </div>
                  {anyFilter ? <Btn variant="ghost" onClick={() => { setDraft(""); setF({ ...DEFAULT_FILTERS, sort: f.sort, dir: f.dir }); }}>{t("reports.campusPulse.text80", "Reset")}</Btn> : null}
                  {order.join() !== DEF_ORDER.join() ? <Btn variant="ghost" onClick={() => setOrder(DEF_ORDER)}>{t("reports.campusPulse.text81", "Reset layout")}</Btn> : null}
                </div>
              </div>

              {selected ? <StudentView v={v} s={selected} onBack={() => setSel(null)} cols={cols} /> : (
                <>
                  <FindingsBand v={v} fnds={fnds} cols={cols}
                    activeFindingId={activeFinding ? activeFinding.id : null} activeQuad={activeQuad}
                    onFinding={(fd) => setScope(fd ? fd.scope : null)}
                    onQuad={(k) => setScope(k ? { quadrant: k } : null)}
                    verbStates={verbStates} onVerb={(fd) => setConfirmVerb(fd)} busyVerb={busyVerb}
                    canAct={canAct} staffNamesVisible={staffNamesVisible} filtered={anyFilter} />

                  {scope ? (
                    <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", padding: "9px 14px", background: T.p4, border: `1px solid ${T.p1}`, borderRadius: 10 }}>
                      <span style={{ fontSize: 12, fontWeight: 700, color: T.p1, whiteSpace: "nowrap" }}>{t("reports.campusPulse.text82", "Scoped to “")}{scopeLabel}” · {v.n.toLocaleString()} {t("reports.campusPulse.text83", "students")}</span>
                      <span style={{ fontSize: 11.5, color: T.n2, ...PRETTY }}>{t("reports.campusPulse.text84", "Every number below — KPIs, medians, the quadrant split and the ladder — was recomputed by the report on this group, so the medians are the group’s own.")}</span>
                      <Btn variant="ghost" onClick={() => setScope(null)}>{t("reports.campusPulse.text85", "Clear")}</Btn>
                    </div>
                  ) : (
                    <div style={{ display: "flex", alignItems: "center", gap: 9, padding: "8px 14px", background: T.cardSoft, border: `1px solid ${T.border}`, borderRadius: 10 }}>
                      <span style={{ ...EYEBROW, fontSize: 9, whiteSpace: "nowrap" }}>{t("reports.campusPulse.text86", "Evidence layer")}</span>
                      <span style={{ fontSize: 11.5, color: T.n2, ...PRETTY }}>{t("reports.campusPulse.text87", "Everything below is the evidence behind the findings above: the KPI band, the semester and program tables, interaction mix, what was measured, departments reached, and the full roster.")}</span>
                    </div>
                  )}

                  <div style={{ display: "grid", gridTemplateColumns: `repeat(${Math.min(6, cols * 2)}, minmax(0, 1fr))`, gap: 12 }}>
                    <Kpi label={t("reports.campusPulse.label16", "Students")} value={v.kpis.students.toLocaleString()} />
                    <Kpi label={t("reports.campusPulse.label", "Interactions")} value={v.interactions.toLocaleString()} />
                    <Kpi label={t("reports.campusPulse.label17", "Median interactions")} value={num(v.kpis.medianInteractionsPerStudent)} />
                    <Kpi label={t("reports.campusPulse.label18", "Median depts touched")} value={num(v.kpis.medianDepartmentsTouched)} />
                    <Kpi label={t("reports.campusPulse.label14", "Walk-in share")} value={pct(v.kpis.walkInShare)} />
                    <Kpi label={t("reports.campusPulse.label19", "Student cancellations")} value={pct(v.kpis.cancelRate)} />
                  </div>

                  <div style={{ display: "grid", gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`, gap: 14, alignItems: "start" }}>
                    {order.map((id) => {
                      const handle = <Handle active={armed === id} onArm={() => setArmed(id)} onDisarm={() => setArmed(null)} />;
                      const body = id === "semester" ? <TermLadder v={v} handle={handle} />
                        : id === "programs" ? <ProgramTable v={v} program={f.program} onProgram={(val) => setF({ ...f, program: val })} handle={handle} />
                        : id === "types" ? <TypePanel v={v} handle={handle} />
                        : id === "coverage" ? <CoveragePanel v={v} handle={handle} />
                        : id === "depts" ? <DeptPanel v={v} handle={handle} />
                        : namesVisible ? (
                          <Roster v={v} onPick={setSel} onSort={onSort} onPage={setPage} handle={handle}
                            activeFinding={activeFinding} assigned={assigned}
                            onAssignRow={(s) => { if (activeFinding) void runVerb(activeFinding, s); }}
                            canExport={canExport} onExport={() => { setPurpose(""); setPurposeErr(false); setExportOpen(true); }}
                            busyRow={busyRow}
                            scopeNote={scopeLabel ? `From card · ${v.roster.total}` : null} />
                        ) : (
                          <div style={WRAP}>
                            <div style={SECTION_HEAD}><span style={{ display: "flex", alignItems: "center", gap: 10 }}>{handle}<span style={CARD_TITLE}>{t("reports.campusPulse.text53", "Student roster")}</span></span></div>
                            <div style={{ padding: "18px 20px", display: "flex", flexDirection: "column", gap: 6 }}>
                              <span style={{ fontSize: 12.5, color: T.n1, fontWeight: 600 }}>{t("reports.campusPulse.text88", "Names sit behind a scope your role does not hold")}</span>
                              <span style={{ fontSize: 12, lineHeight: 1.5, color: T.n2, ...PRETTY }}>
                                {t("reports.campusPulse.text89", "Every aggregate on this page is open to you — findings, medians, the program table and the semester ladder all read normally. The roster carries named students with record ids, so it needs the customer-records capability. The report itself withholds the names; they are not fetched and hidden here.")}</span>
                            </div>
                          </div>
                        );
                      return (
                        <div key={id} draggable={armed === id}
                          onDragStart={(e) => { setDragId(id); try { e.dataTransfer.effectAllowed = "move"; e.dataTransfer.setData("text/plain", id); } catch { /* ignore */ } }}
                          onDragEnd={() => { setDragId(null); setOverId(null); setArmed(null); }}
                          onDragOver={(e) => { if (dragId && dragId !== id) { e.preventDefault(); setOverId(id); } }}
                          onDragLeave={() => setOverId((x) => (x === id ? null : x))}
                          onDrop={(e) => { e.preventDefault(); moveSection(dragId, id); setDragId(null); setOverId(null); setArmed(null); }}
                          style={{ gridColumn: `span ${Math.min(cols, SPANS[id])}`, minWidth: 0, borderRadius: 10, opacity: dragId === id ? 0.4 : 1, outline: overId === id && dragId && dragId !== id ? `2px dashed ${T.p1}` : "none", outlineOffset: 4 }}>
                          {body}
                        </div>
                      );
                    })}
                  </div>
                </>
              )}
            </>
          )}
      </div>

      <ConfirmModal open={!!confirmVerb} title={confirmVerb && confirmVerb.verb ? confirmVerb.verb.label : t("reports.campusPulse.title23", "Hand this over")}
        confirmLabel={confirmVerb && confirmVerb.verb && confirmVerb.verb.kind === "handoff" ? t("reports.campusPulse.confirmLabel", "Record the hand-off") : t("reports.campusPulse.confirmLabel2", "Record")}
        busy={!!busyVerb}
        onClose={() => setConfirmVerb(null)}
        onConfirm={() => { const fd = confirmVerb; setConfirmVerb(null); if (fd) void runVerb(fd); }}
        message={confirmVerb ? (
          <span style={{ display: "flex", flexDirection: "column", gap: 8, fontSize: 12.5, lineHeight: 1.5, color: T.n2 }}>
            <span style={{ color: T.n1, fontWeight: 600 }}>{confirmVerb.action}</span>
            <span>{t("reports.campusPulse.text90", "Owner —")}{confirmVerb.owner}</span>
            {confirmVerb.verbBlocked
              ? <span style={{ color: T.orange1 }}>{confirmVerb.verbBlocked}</span>
              : confirmVerb.verb && confirmVerb.verb.kind === "handoff"
                ? <span>{HANDOFF_UNAVAILABLE}</span>
                : <span>{confirmVerb.memberCount === null ? t("reports.campusPulse.text91", "The students this finding names") : plural(confirmVerb.memberCount, "student")} {t("reports.campusPulse.text92", "— the report is re-read scoped to this finding so every member is covered, each one is written to their own record, and the batch is recorded in the audit trail with you as the actor.")}</span>}
            <span style={{ ...MONO, fontSize: 10.5, color: T.soft }}>{confirmVerb.method}</span>
          </span>
        ) : null} />

      <ConfirmModal open={exportOpen} title={t("reports.campusPulse.title24", "Export the roster")} confirmLabel={t("reports.campusPulse.confirmLabel3", "Export and log")}
        onClose={() => { setExportOpen(false); setPurposeErr(false); }} onConfirm={() => { void doExport(); }}
        message={v ? (
          <span style={{ display: "flex", flexDirection: "column", gap: 9, fontSize: 12.5, lineHeight: 1.5, color: T.n2 }}>
            <span><strong style={{ color: T.n1 }}>{plural(v.roster.total, "row")}</strong> {t("reports.campusPulse.text93", "— named students, record ids, assigned advisor, why each was flagged, and any hand-off already recorded against them. The whole scoped roster is re-read for the file, not just the page on screen.")}</span>
            <span style={{ ...MONO, fontSize: 10.5, color: T.soft }}>
              {v.cohortLabel} · {f.program === "all" ? t("reports.campusPulse.text94", "all programs") : f.program}{scopeLabel ? t("reports.campusPulse.text95", "· scope: {scopeLabel}", { scopeLabel }) : ""}
            </span>
            <span>{t("reports.campusPulse.text96", "The file is stamped with your name, the time, this scope and your purpose; the same record goes to the audit trail.")}</span>
            <span style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              <span style={{ ...EYEBROW, fontSize: 9 }}>{t("reports.campusPulse.text97", "Purpose — required")}</span>
              <textarea value={purpose} onChange={(e) => { setPurpose(e.target.value); setPurposeErr(false); }} rows={2}
                placeholder={t("reports.campusPulse.placeholder3", "e.g. Term-gap outreach list for advising ops, week of…")}
                style={{ width: "100%", boxSizing: "border-box", resize: "vertical", fontFamily: "inherit", fontSize: 12.5, color: T.n1, padding: "8px 10px", borderRadius: 8, border: `1px solid ${purposeErr ? T.red1 : T.border}`, outline: "none" }} />
              {purposeErr ? <span style={{ fontSize: 11, color: T.red1 }}>{t("reports.campusPulse.text98", "A purpose is required before a named-student file is produced.")}</span> : null}
            </span>
          </span>
        ) : null} />
    </Screen>
  );
}
